import React, { useState, useRef } from "react";
import { useReactToPrint } from "react-to-print";
import "./Resume.css";
import { templates } from "./templates/index";

const ResumeBuilder = () => {
  // ---------- Mobile Toggle ----------
  const [viewMode, setViewMode] = useState("edit"); // "edit" or "preview"

  // ---------- Zoom State ----------
  const getDefaultZoom = () => {
    const width = window.innerWidth;
    if (width >= 1400) return 0.75;
    if (width >= 1200) return 0.68;
    if (width >= 992) return 0.62;
    return 0.55;
  };
  const [zoomLevel, setZoomLevel] = useState(getDefaultZoom());

  const zoomIn = () => {
    setZoomLevel((prev) => Math.min(prev + 0.05, 1.2));
  };
  const zoomOut = () => {
    setZoomLevel((prev) => Math.max(prev - 0.05, 0.4));
  };
  const resetZoom = () => {
    setZoomLevel(getDefaultZoom());
  };

  // ---------- Form State ----------
  const [activeSection, setActiveSection] = useState("personal");
  const [template, setTemplate] = useState("modern");
  const [isPrinting, setIsPrinting] = useState(false);
  const resumeRef = useRef();

  // Helper for stable IDs
  const nextId = useRef(1);
  const getNextId = () => nextId.current++;

  // ---------- Resume Data ----------
  const [resumeData, setResumeData] = useState({
    personal: {
      fullName: "",
      email: "",
      phone: "",
      address: "",
      linkedin: "",
      portfolio: "",
      title: "",
      summary: "",
      photo: null,
    },
    education: [
      {
        id: getNextId(),
        degree: "",
        institution: "",
        year: "",
        percentage: "",
        location: "",
      },
    ],
    experience: [
      {
        id: getNextId(),
        title: "",
        company: "",
        location: "",
        startDate: "",
        endDate: "",
        current: false,
        description: "",
      },
    ],
    skills: {
      technical: [],
      soft: [],
      languages: [],
    },
    projects: [
      {
        id: getNextId(),
        name: "",
        technologies: "",
        description: "",
        link: "",
      },
    ],
    certifications: [
      {
        id: getNextId(),
        name: "",
        issuer: "",
        year: "",
        link: "",
      },
    ],
    achievements: [],
    interests: [],
  });

  // Input states for dynamic lists
  const [newSkill, setNewSkill] = useState("");
  const [newSoftSkill, setNewSoftSkill] = useState("");
  const [newLanguage, setNewLanguage] = useState("");
  const [newAchievement, setNewAchievement] = useState("");
  const [newInterest, setNewInterest] = useState("");

  // ========== Handlers ==========
  const handlePersonalChange = (e) => {
    const { name, value } = e.target;
    setResumeData((prev) => ({
      ...prev,
      personal: { ...prev.personal, [name]: value },
    }));
  };

  const handlePhotoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setResumeData((prev) => ({
          ...prev,
          personal: { ...prev.personal, photo: reader.result },
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemovePhoto = () => {
    setResumeData((prev) => ({
      ...prev,
      personal: { ...prev.personal, photo: null },
    }));
  };

  // Education
  const addEducation = () => {
    setResumeData((prev) => ({
      ...prev,
      education: [
        ...prev.education,
        {
          id: getNextId(),
          degree: "",
          institution: "",
          year: "",
          percentage: "",
          location: "",
        },
      ],
    }));
  };

  const updateEducation = (id, field, value) => {
    setResumeData((prev) => ({
      ...prev,
      education: prev.education.map((edu) =>
        edu.id === id ? { ...edu, [field]: value } : edu,
      ),
    }));
  };

  const removeEducation = (id) => {
    setResumeData((prev) => ({
      ...prev,
      education: prev.education.filter((edu) => edu.id !== id),
    }));
  };

  // Experience
  const addExperience = () => {
    setResumeData((prev) => ({
      ...prev,
      experience: [
        ...prev.experience,
        {
          id: getNextId(),
          title: "",
          company: "",
          location: "",
          startDate: "",
          endDate: "",
          current: false,
          description: "",
        },
      ],
    }));
  };

  const updateExperience = (id, field, value) => {
    setResumeData((prev) => ({
      ...prev,
      experience: prev.experience.map((exp) =>
        exp.id === id ? { ...exp, [field]: value } : exp,
      ),
    }));
  };

  const removeExperience = (id) => {
    setResumeData((prev) => ({
      ...prev,
      experience: prev.experience.filter((exp) => exp.id !== id),
    }));
  };

  // Skills
  const addTechnicalSkill = () => {
    if (newSkill.trim()) {
      setResumeData((prev) => ({
        ...prev,
        skills: {
          ...prev.skills,
          technical: [...prev.skills.technical, newSkill.trim()],
        },
      }));
      setNewSkill("");
    }
  };

  const removeTechnicalSkill = (skill) => {
    setResumeData((prev) => ({
      ...prev,
      skills: {
        ...prev.skills,
        technical: prev.skills.technical.filter((s) => s !== skill),
      },
    }));
  };

  const addSoftSkill = () => {
    if (newSoftSkill.trim()) {
      setResumeData((prev) => ({
        ...prev,
        skills: {
          ...prev.skills,
          soft: [...prev.skills.soft, newSoftSkill.trim()],
        },
      }));
      setNewSoftSkill("");
    }
  };

  const removeSoftSkill = (skill) => {
    setResumeData((prev) => ({
      ...prev,
      skills: {
        ...prev.skills,
        soft: prev.skills.soft.filter((s) => s !== skill),
      },
    }));
  };

  const addLanguage = () => {
    if (newLanguage.trim()) {
      setResumeData((prev) => ({
        ...prev,
        skills: {
          ...prev.skills,
          languages: [...prev.skills.languages, newLanguage.trim()],
        },
      }));
      setNewLanguage("");
    }
  };

  const removeLanguage = (lang) => {
    setResumeData((prev) => ({
      ...prev,
      skills: {
        ...prev.skills,
        languages: prev.skills.languages.filter((l) => l !== lang),
      },
    }));
  };

  // Projects
  const addProject = () => {
    setResumeData((prev) => ({
      ...prev,
      projects: [
        ...prev.projects,
        {
          id: getNextId(),
          name: "",
          technologies: "",
          description: "",
          link: "",
        },
      ],
    }));
  };

  const updateProject = (id, field, value) => {
    setResumeData((prev) => ({
      ...prev,
      projects: prev.projects.map((proj) =>
        proj.id === id ? { ...proj, [field]: value } : proj,
      ),
    }));
  };

  const removeProject = (id) => {
    setResumeData((prev) => ({
      ...prev,
      projects: prev.projects.filter((proj) => proj.id !== id),
    }));
  };

  // Certifications
  const addCertification = () => {
    setResumeData((prev) => ({
      ...prev,
      certifications: [
        ...prev.certifications,
        {
          id: getNextId(),
          name: "",
          issuer: "",
          year: "",
          link: "",
        },
      ],
    }));
  };

  const updateCertification = (id, field, value) => {
    setResumeData((prev) => ({
      ...prev,
      certifications: prev.certifications.map((cert) =>
        cert.id === id ? { ...cert, [field]: value } : cert,
      ),
    }));
  };

  const removeCertification = (id) => {
    setResumeData((prev) => ({
      ...prev,
      certifications: prev.certifications.filter((cert) => cert.id !== id),
    }));
  };

  // Achievements
  const addAchievement = () => {
    if (newAchievement.trim()) {
      setResumeData((prev) => ({
        ...prev,
        achievements: [...prev.achievements, newAchievement.trim()],
      }));
      setNewAchievement("");
    }
  };

  const removeAchievement = (achievement) => {
    setResumeData((prev) => ({
      ...prev,
      achievements: prev.achievements.filter((a) => a !== achievement),
    }));
  };

  // Interests
  const addInterest = () => {
    if (newInterest.trim()) {
      setResumeData((prev) => ({
        ...prev,
        interests: [...prev.interests, newInterest.trim()],
      }));
      setNewInterest("");
    }
  };

  const removeInterest = (interest) => {
    setResumeData((prev) => ({
      ...prev,
      interests: prev.interests.filter((i) => i !== interest),
    }));
  };

  // Load sample data
  const loadSampleData = () => {
    nextId.current = 100;
    setResumeData({
      personal: {
        fullName: "Alex Johnson",
        email: "alex.johnson@example.com",
        phone: "+1 (555) 123-4567",
        address: "San Francisco, CA",
        linkedin: "linkedin.com/in/alexjohnson",
        portfolio: "alexjohnson.dev",
        title: "Senior Full Stack Developer",
        summary:
          "Results-driven Full Stack Developer with 8+ years of experience building scalable web applications. Passionate about clean code, user experience, and mentoring junior developers.",
        photo: null,
      },
      education: [
        {
          id: getNextId(),
          degree: "M.Sc. in Computer Science",
          institution: "Stanford University",
          year: "2014-2016",
          percentage: "3.9 GPA",
          location: "Stanford, CA",
        },
        {
          id: getNextId(),
          degree: "B.E. in Information Technology",
          institution: "University of Washington",
          year: "2010-2014",
          percentage: "3.7 GPA",
          location: "Seattle, WA",
        },
      ],
      experience: [
        {
          id: getNextId(),
          title: "Senior Full Stack Developer",
          company: "TechCorp Inc.",
          location: "San Francisco, CA",
          startDate: "Jan 2021",
          endDate: "Present",
          current: true,
          description:
            "Lead development of microservices architecture using React, Node.js, and AWS. Improved performance by 40% and reduced deployment time by 60%. Mentored 3 junior developers.",
        },
        {
          id: getNextId(),
          title: "Frontend Developer",
          company: "WebSolutions LLC",
          location: "Seattle, WA",
          startDate: "Jun 2016",
          endDate: "Dec 2020",
          current: false,
          description:
            "Built responsive web applications with React and Redux. Collaborated with designers to implement pixel-perfect UIs. Achieved 99% accessibility score.",
        },
      ],
      skills: {
        technical: [
          "React",
          "Node.js",
          "TypeScript",
          "Python",
          "GraphQL",
          "AWS",
          "MongoDB",
        ],
        soft: [
          "Team Leadership",
          "Agile Methodologies",
          "Problem Solving",
          "Communication",
        ],
        languages: ["English (Native)", "Spanish (Conversational)"],
      },
      projects: [
        {
          id: getNextId(),
          name: "E‑Commerce Platform",
          technologies: "React, Redux, Stripe API, Firebase",
          description:
            "Developed a full-featured online store with cart, checkout, and admin dashboard. Handled 5k+ monthly users.",
          link: "https://github.com/alex/ecommerce",
        },
        {
          id: getNextId(),
          name: "Task Management App",
          technologies: "Node.js, Express, MongoDB, React",
          description:
            "Created a collaborative task manager with real-time updates and role-based permissions.",
          link: "https://github.com/alex/taskmanager",
        },
      ],
      certifications: [
        {
          id: getNextId(),
          name: "AWS Certified Solutions Architect",
          issuer: "Amazon Web Services",
          year: "2022",
          link: "https://aws.amazon.com/certification/",
        },
        {
          id: getNextId(),
          name: "Certified ScrumMaster (CSM)",
          issuer: "Scrum Alliance",
          year: "2020",
          link: "https://www.scrumalliance.org/",
        },
      ],
      achievements: [
        "Winner of Hackathon 2021 – Best AI Application",
        'Published article on "Modern Web Development" in Dev.to (10k+ reads)',
        "Open Source Contributor to React and Next.js projects",
      ],
      interests: ["Open Source", "Machine Learning", "Photography", "Hiking"],
    });
  };

  // Reset form
  const resetForm = () => {
    if (window.confirm("Are you sure you want to reset all data?")) {
      nextId.current = 1;
      setResumeData({
        personal: {
          fullName: "",
          email: "",
          phone: "",
          address: "",
          linkedin: "",
          portfolio: "",
          title: "",
          summary: "",
          photo: null,
        },
        education: [
          {
            id: getNextId(),
            degree: "",
            institution: "",
            year: "",
            percentage: "",
            location: "",
          },
        ],
        experience: [
          {
            id: getNextId(),
            title: "",
            company: "",
            location: "",
            startDate: "",
            endDate: "",
            current: false,
            description: "",
          },
        ],
        skills: { technical: [], soft: [], languages: [] },
        projects: [
          {
            id: getNextId(),
            name: "",
            technologies: "",
            description: "",
            link: "",
          },
        ],
        certifications: [
          {
            id: getNextId(),
            name: "",
            issuer: "",
            year: "",
            link: "",
          },
        ],
        achievements: [],
        interests: [],
      });
      setNewSkill("");
      setNewSoftSkill("");
      setNewLanguage("");
      setNewAchievement("");
      setNewInterest("");
    }
  };

  // ========== Print using react-to-print ==========
  const handlePrint = useReactToPrint({
    contentRef: resumeRef,
    documentTitle: `${resumeData.personal.fullName || "Resume"} - Print`,
    onBeforeGetContent: () => {
      setIsPrinting(true);
      return Promise.resolve();
    },
    onAfterPrint: () => {
      setIsPrinting(false);
    },
    onPrintError: (error) => {
      console.error("Print error:", error);
      alert("Failed to print. Please try again.");
      setIsPrinting(false);
    },
  });

  // ========== Sections and Template ==========
  const sections = [
    { id: "personal", label: "Personal Info", icon: "👤" },
    { id: "education", label: "Education", icon: "🎓" },
    { id: "experience", label: "Experience", icon: "💼" },
    { id: "skills", label: "Skills", icon: "⚡" },
    { id: "projects", label: "Projects", icon: "🚀" },
    { id: "certifications", label: "Certifications", icon: "📜" },
    { id: "achievements", label: "Achievements", icon: "🏆" },
    { id: "interests", label: "Interests", icon: "🎯" },
  ];

  const currentTemplate = templates.find((t) => t.id === template);
  const TemplateComponent =
    currentTemplate?.component || templates[0]?.component;

  // ========== JSX ==========
  return (
    <>
      {isPrinting && (
        <div className="pdf-loader-overlay">
          <div className="loader-content">
            <div className="spinner"></div>
            <p>Preparing Print...</p>
            <p className="loader-hint">Please wait, opening print dialog.</p>
          </div>
        </div>
      )}

      <div className="resume-builder">
        {/* Mobile Toggle Bar */}
        <div className="mobile-toggle-bar">
          <button
            className={viewMode === "edit" ? "active" : ""}
            onClick={() => setViewMode("edit")}
          >
            📝 Edit Resume
          </button>
          <button
            className={viewMode === "preview" ? "active" : ""}
            onClick={() => setViewMode("preview")}
          >
            👁️ Preview
          </button>
        </div>

        <div className="resume-container">
          {/* Left Sidebar - Form */}
          <div
            className={`resume-form-sidebar ${
              viewMode === "edit" ? "mobile-visible" : "mobile-hidden"
            }`}
          >
            <div className="template-selector">
              <label>Choose Template:</label>
              <select
                value={template}
                onChange={(e) => setTemplate(e.target.value)}
              >
                {templates.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.label} – {t.description}
                  </option>
                ))}
              </select>
            </div>

            <div className="section-tabs">
              {sections.map((section) => (
                <button
                  key={section.id}
                  className={`section-tab ${activeSection === section.id ? "active" : ""}`}
                  onClick={() => setActiveSection(section.id)}
                >
                  <span className="tab-icon">{section.icon}</span>
                  <span className="tab-label">{section.label}</span>
                </button>
              ))}
            </div>

            <div className="form-content">
              {/* Personal Information */}
              {activeSection === "personal" && (
                <div className="form-section">
                  <h3>Personal Information</h3>
                  <div className="form-group">
                    <label>Full Name *</label>
                    <input
                      type="text"
                      name="fullName"
                      value={resumeData.personal.fullName}
                      onChange={handlePersonalChange}
                      placeholder="John Doe"
                    />
                  </div>
                  <div className="form-group">
                    <label>Professional Title</label>
                    <input
                      type="text"
                      name="title"
                      value={resumeData.personal.title}
                      onChange={handlePersonalChange}
                      placeholder="Frontend Developer"
                    />
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Email *</label>
                      <input
                        type="email"
                        name="email"
                        value={resumeData.personal.email}
                        onChange={handlePersonalChange}
                        placeholder="john@example.com"
                      />
                    </div>
                    <div className="form-group">
                      <label>Phone *</label>
                      <input
                        type="tel"
                        name="phone"
                        value={resumeData.personal.phone}
                        onChange={handlePersonalChange}
                        placeholder="+1 234 567 8900"
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Address</label>
                    <input
                      type="text"
                      name="address"
                      value={resumeData.personal.address}
                      onChange={handlePersonalChange}
                      placeholder="City, Country"
                    />
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>LinkedIn</label>
                      <input
                        type="text"
                        name="linkedin"
                        value={resumeData.personal.linkedin}
                        onChange={handlePersonalChange}
                        placeholder="linkedin.com/in/johndoe"
                      />
                    </div>
                    <div className="form-group">
                      <label>Portfolio</label>
                      <input
                        type="text"
                        name="portfolio"
                        value={resumeData.personal.portfolio}
                        onChange={handlePersonalChange}
                        placeholder="johndoe.com"
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Profile Photo (Optional)</label>
                    <div
                      style={{
                        display: "flex",
                        gap: "10px",
                        alignItems: "center",
                        flexWrap: "wrap",
                      }}
                    >
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handlePhotoUpload}
                      />
                      {resumeData.personal.photo && (
                        <button
                          type="button"
                          onClick={handleRemovePhoto}
                          className="remove-photo-btn"
                        >
                          Remove Photo
                        </button>
                      )}
                    </div>
                    {resumeData.personal.photo && (
                      <div style={{ marginTop: "10px" }}>
                        <img
                          src={resumeData.personal.photo}
                          alt="Profile Preview"
                          style={{
                            width: "60px",
                            height: "60px",
                            objectFit: "cover",
                            borderRadius: "50%",
                          }}
                        />
                      </div>
                    )}
                  </div>
                  <div className="form-group">
                    <label>Professional Summary</label>
                    <textarea
                      name="summary"
                      value={resumeData.personal.summary}
                      onChange={handlePersonalChange}
                      rows="4"
                      placeholder="Write a brief summary of your professional background..."
                    />
                  </div>
                </div>
              )}

              {/* Education */}
              {activeSection === "education" && (
                <div className="form-section">
                  <h3>Education</h3>
                  {resumeData.education.map((edu, index) => (
                    <div key={edu.id} className="education-item card-item">
                      <div className="item-header">
                        <h4>Education {index + 1}</h4>
                        {resumeData.education.length > 1 && (
                          <button
                            onClick={() => removeEducation(edu.id)}
                            className="remove-btn"
                          >
                            ✕
                          </button>
                        )}
                      </div>
                      <div className="form-group">
                        <label>Degree *</label>
                        <input
                          type="text"
                          value={edu.degree}
                          onChange={(e) =>
                            updateEducation(edu.id, "degree", e.target.value)
                          }
                          placeholder="B.Tech in Computer Science"
                        />
                      </div>
                      <div className="form-group">
                        <label>Institution *</label>
                        <input
                          type="text"
                          value={edu.institution}
                          onChange={(e) =>
                            updateEducation(
                              edu.id,
                              "institution",
                              e.target.value,
                            )
                          }
                          placeholder="University Name"
                        />
                      </div>
                      <div className="form-row">
                        <div className="form-group">
                          <label>Year</label>
                          <input
                            type="text"
                            value={edu.year}
                            onChange={(e) =>
                              updateEducation(edu.id, "year", e.target.value)
                            }
                            placeholder="2020-2024"
                          />
                        </div>
                        <div className="form-group">
                          <label>Percentage/CGPA</label>
                          <input
                            type="text"
                            value={edu.percentage}
                            onChange={(e) =>
                              updateEducation(
                                edu.id,
                                "percentage",
                                e.target.value,
                              )
                            }
                            placeholder="8.5 CGPA / 85%"
                          />
                        </div>
                      </div>
                      <div className="form-group">
                        <label>Location</label>
                        <input
                          type="text"
                          value={edu.location}
                          onChange={(e) =>
                            updateEducation(edu.id, "location", e.target.value)
                          }
                          placeholder="City, Country"
                        />
                      </div>
                    </div>
                  ))}
                  <button onClick={addEducation} className="add-btn">
                    + Add Education
                  </button>
                </div>
              )}

              {/* Experience */}
              {activeSection === "experience" && (
                <div className="form-section">
                  <h3>Work Experience</h3>
                  {resumeData.experience.map((exp, index) => (
                    <div key={exp.id} className="experience-item card-item">
                      <div className="item-header">
                        <h4>Experience {index + 1}</h4>
                        {resumeData.experience.length > 1 && (
                          <button
                            onClick={() => removeExperience(exp.id)}
                            className="remove-btn"
                          >
                            ✕
                          </button>
                        )}
                      </div>
                      <div className="form-group">
                        <label>Job Title *</label>
                        <input
                          type="text"
                          value={exp.title}
                          onChange={(e) =>
                            updateExperience(exp.id, "title", e.target.value)
                          }
                          placeholder="Frontend Developer"
                        />
                      </div>
                      <div className="form-group">
                        <label>Company *</label>
                        <input
                          type="text"
                          value={exp.company}
                          onChange={(e) =>
                            updateExperience(exp.id, "company", e.target.value)
                          }
                          placeholder="Company Name"
                        />
                      </div>
                      <div className="form-row">
                        <div className="form-group">
                          <label>Start Date</label>
                          <input
                            type="text"
                            value={exp.startDate}
                            onChange={(e) =>
                              updateExperience(
                                exp.id,
                                "startDate",
                                e.target.value,
                              )
                            }
                            placeholder="Jan 2022"
                          />
                        </div>
                        <div className="form-group">
                          <label>End Date</label>
                          <input
                            type="text"
                            value={exp.endDate}
                            onChange={(e) =>
                              updateExperience(
                                exp.id,
                                "endDate",
                                e.target.value,
                              )
                            }
                            placeholder="Present"
                            disabled={exp.current}
                          />
                        </div>
                      </div>
                      <div className="form-group checkbox-group">
                        <label>
                          <input
                            type="checkbox"
                            checked={exp.current}
                            onChange={(e) =>
                              updateExperience(
                                exp.id,
                                "current",
                                e.target.checked,
                              )
                            }
                          />
                          I currently work here
                        </label>
                      </div>
                      <div className="form-group">
                        <label>Description</label>
                        <textarea
                          value={exp.description}
                          onChange={(e) =>
                            updateExperience(
                              exp.id,
                              "description",
                              e.target.value,
                            )
                          }
                          rows="3"
                          placeholder="Describe your responsibilities and achievements..."
                        />
                      </div>
                    </div>
                  ))}
                  <button onClick={addExperience} className="add-btn">
                    + Add Experience
                  </button>
                </div>
              )}

              {/* Skills */}
              {activeSection === "skills" && (
                <div className="form-section">
                  <h3>Skills</h3>
                  <div className="skills-section">
                    <h4>Technical Skills</h4>
                    <div className="skills-input-group">
                      <input
                        type="text"
                        value={newSkill}
                        onChange={(e) => setNewSkill(e.target.value)}
                        placeholder="e.g., React.js"
                        onKeyPress={(e) =>
                          e.key === "Enter" && addTechnicalSkill()
                        }
                      />
                      <button
                        onClick={addTechnicalSkill}
                        className="add-small-btn"
                      >
                        Add
                      </button>
                    </div>
                    <div className="skills-tags">
                      {resumeData.skills.technical.map((skill, idx) => (
                        <span key={idx} className="skill-tag">
                          {skill}
                          <button onClick={() => removeTechnicalSkill(skill)}>
                            ✕
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="skills-section">
                    <h4>Soft Skills</h4>
                    <div className="skills-input-group">
                      <input
                        type="text"
                        value={newSoftSkill}
                        onChange={(e) => setNewSoftSkill(e.target.value)}
                        placeholder="e.g., Team Leadership"
                        onKeyPress={(e) => e.key === "Enter" && addSoftSkill()}
                      />
                      <button onClick={addSoftSkill} className="add-small-btn">
                        Add
                      </button>
                    </div>
                    <div className="skills-tags">
                      {resumeData.skills.soft.map((skill, idx) => (
                        <span key={idx} className="skill-tag soft">
                          {skill}
                          <button onClick={() => removeSoftSkill(skill)}>
                            ✕
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="skills-section">
                    <h4>Languages</h4>
                    <div className="skills-input-group">
                      <input
                        type="text"
                        value={newLanguage}
                        onChange={(e) => setNewLanguage(e.target.value)}
                        placeholder="e.g., English (Fluent)"
                        onKeyPress={(e) => e.key === "Enter" && addLanguage()}
                      />
                      <button onClick={addLanguage} className="add-small-btn">
                        Add
                      </button>
                    </div>
                    <div className="skills-tags">
                      {resumeData.skills.languages.map((lang, idx) => (
                        <span key={idx} className="skill-tag language">
                          {lang}
                          <button onClick={() => removeLanguage(lang)}>
                            ✕
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Projects */}
              {activeSection === "projects" && (
                <div className="form-section">
                  <h3>Projects</h3>
                  {resumeData.projects.map((proj, index) => (
                    <div key={proj.id} className="project-item card-item">
                      <div className="item-header">
                        <h4>Project {index + 1}</h4>
                        {resumeData.projects.length > 1 && (
                          <button
                            onClick={() => removeProject(proj.id)}
                            className="remove-btn"
                          >
                            ✕
                          </button>
                        )}
                      </div>
                      <div className="form-group">
                        <label>Project Name *</label>
                        <input
                          type="text"
                          value={proj.name}
                          onChange={(e) =>
                            updateProject(proj.id, "name", e.target.value)
                          }
                          placeholder="E-commerce Website"
                        />
                      </div>
                      <div className="form-group">
                        <label>Technologies Used</label>
                        <input
                          type="text"
                          value={proj.technologies}
                          onChange={(e) =>
                            updateProject(
                              proj.id,
                              "technologies",
                              e.target.value,
                            )
                          }
                          placeholder="React, Node.js, MongoDB"
                        />
                      </div>
                      <div className="form-group">
                        <label>Description</label>
                        <textarea
                          value={proj.description}
                          onChange={(e) =>
                            updateProject(
                              proj.id,
                              "description",
                              e.target.value,
                            )
                          }
                          rows="3"
                          placeholder="Describe the project, your role, and achievements..."
                        />
                      </div>
                      <div className="form-group">
                        <label>Project Link (Optional)</label>
                        <input
                          type="text"
                          value={proj.link}
                          onChange={(e) =>
                            updateProject(proj.id, "link", e.target.value)
                          }
                          placeholder="https://github.com/yourproject"
                        />
                      </div>
                    </div>
                  ))}
                  <button onClick={addProject} className="add-btn">
                    + Add Project
                  </button>
                </div>
              )}

              {/* Certifications */}
              {activeSection === "certifications" && (
                <div className="form-section">
                  <h3>Certifications</h3>
                  {resumeData.certifications.map((cert, index) => (
                    <div key={cert.id} className="certification-item card-item">
                      <div className="item-header">
                        <h4>Certification {index + 1}</h4>
                        {resumeData.certifications.length > 1 && (
                          <button
                            onClick={() => removeCertification(cert.id)}
                            className="remove-btn"
                          >
                            ✕
                          </button>
                        )}
                      </div>
                      <div className="form-group">
                        <label>Certification Name *</label>
                        <input
                          type="text"
                          value={cert.name}
                          onChange={(e) =>
                            updateCertification(cert.id, "name", e.target.value)
                          }
                          placeholder="AWS Certified Developer"
                        />
                      </div>
                      <div className="form-group">
                        <label>Issuing Organization</label>
                        <input
                          type="text"
                          value={cert.issuer}
                          onChange={(e) =>
                            updateCertification(
                              cert.id,
                              "issuer",
                              e.target.value,
                            )
                          }
                          placeholder="Amazon Web Services"
                        />
                      </div>
                      <div className="form-row">
                        <div className="form-group">
                          <label>Year</label>
                          <input
                            type="text"
                            value={cert.year}
                            onChange={(e) =>
                              updateCertification(
                                cert.id,
                                "year",
                                e.target.value,
                              )
                            }
                            placeholder="2023"
                          />
                        </div>
                        <div className="form-group">
                          <label>Certificate Link</label>
                          <input
                            type="text"
                            value={cert.link}
                            onChange={(e) =>
                              updateCertification(
                                cert.id,
                                "link",
                                e.target.value,
                              )
                            }
                            placeholder="https://certification.link"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                  <button onClick={addCertification} className="add-btn">
                    + Add Certification
                  </button>
                </div>
              )}

              {/* Achievements */}
              {activeSection === "achievements" && (
                <div className="form-section">
                  <h3>Achievements</h3>
                  <div className="list-input-group">
                    <div className="skills-input-group">
                      <input
                        type="text"
                        value={newAchievement}
                        onChange={(e) => setNewAchievement(e.target.value)}
                        placeholder="e.g., Won Hackathon 2023"
                        onKeyPress={(e) =>
                          e.key === "Enter" && addAchievement()
                        }
                      />
                      <button
                        onClick={addAchievement}
                        className="add-small-btn"
                      >
                        Add
                      </button>
                    </div>
                    <div className="list-items">
                      {resumeData.achievements.map((achievement, idx) => (
                        <div key={idx} className="list-item">
                          <span>🏆 {achievement}</span>
                          <button
                            onClick={() => removeAchievement(achievement)}
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Interests */}
              {activeSection === "interests" && (
                <div className="form-section">
                  <h3>Interests & Hobbies</h3>
                  <div className="list-input-group">
                    <div className="skills-input-group">
                      <input
                        type="text"
                        value={newInterest}
                        onChange={(e) => setNewInterest(e.target.value)}
                        placeholder="e.g., Reading, Photography"
                        onKeyPress={(e) => e.key === "Enter" && addInterest()}
                      />
                      <button onClick={addInterest} className="add-small-btn">
                        Add
                      </button>
                    </div>
                    <div className="list-items">
                      {resumeData.interests.map((interest, idx) => (
                        <div key={idx} className="list-item">
                          <span>🎯 {interest}</span>
                          <button onClick={() => removeInterest(interest)}>
                            ✕
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="form-actions">
              <button onClick={loadSampleData} className="sample-btn">
                Load Sample Data
              </button>
              <button onClick={resetForm} className="reset-btn">
                Reset All
              </button>
              <button
                onClick={handlePrint}
                className="download-btn"
                disabled={isPrinting}
              >
                {isPrinting ? "⏳ Preparing Print..." : "🖨️ Print Resume"}
              </button>
            </div>
          </div>

          {/* Right Side - Preview */}
          <div
            className={`resume-preview ${
              viewMode === "preview" ? "mobile-visible" : "mobile-hidden"
            }`}
          >
            <div className="preview-header">
              <h3>Live Preview</h3>
              <div className="zoom-controls">
                <button onClick={zoomOut} title="Zoom Out">
                  −
                </button>
                <span>{Math.round(zoomLevel * 100)}%</span>
                <button onClick={zoomIn} title="Zoom In">
                  +
                </button>
                <button onClick={resetZoom} title="Reset Zoom">
                  ⟳
                </button>
              </div>
              <span className="preview-badge">
                {currentTemplate?.label} Template
              </span>
              <button
                onClick={handlePrint}
                className="download-pdf-btn"
                disabled={isPrinting}
              >
                {isPrinting ? "⏳ Preparing..." : "🖨️ Print"}
              </button>
            </div>
            <div className="preview-container" ref={resumeRef}>
              <div
                className="resume-template"
                style={{
                  transform: `scale(${zoomLevel})`,
                  transformOrigin: "top center",
                }}
              >
                {TemplateComponent && (
                  <TemplateComponent resumeData={resumeData} />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ResumeBuilder;
